import { useState, type ReactNode } from 'react'
import { Box, Dialog, DialogContent, Typography } from '@mui/material'
import { PhotoSlider } from './PhotoSlider.js'

/**
 * Taille d'identification, commune à TOUTES les fiches.
 *
 * ⭐ C'est la fiche CLIENT qui fait référence : un entête sert à identifier
 * d'un coup d'œil, pas à exposer une photo. 56 px suffisent pour reconnaître,
 * et gardent la carte compacte — donc plus de contenu utile à l'écran.
 */
export const VIGNETTE_FICHE = 56

/**
 * La vignette d'entête d'une fiche — article, client ou fournisseur.
 *
 * Elle existe pour que les trois fiches soient identiques. Chacune dessinait
 * la sienne : un Avatar rond de 56 pour le client, un Avatar carré de 64 pour
 * le fournisseur, un pavé de 104×136 avec carrousel pour l'article. D'où des
 * cartes d'entête de hauteurs différentes et une photo qu'on pouvait agrandir
 * ici mais pas là.
 *
 * Mutualiser n'est pas seulement plus court : c'est ce qui empêche les trois
 * fiches de rediverger à la prochaine retouche.
 *
 * Qui veut vraiment REGARDER la photo clique : le carrousel s'ouvre alors en
 * grand. La petite taille ne coûte donc aucune information.
 */
export function VignetteFiche({
  images,
  repli,
  forme = 'ronde',
}: {
  /** Visuels disponibles. Vide : la vignette n'est pas cliquable. */
  images: string[]
  /** Ce qu'on montre faute de visuel : une icône, des initiales. */
  repli: ReactNode
  /**
   * Ronde pour une PERSONNE, carrée à coins arrondis pour une CHOSE.
   *
   * Ce n'est pas une fantaisie : un cercle rogne les quatre coins, ce qui
   * convient à un portrait — cadré au centre — mais ampute un produit
   * photographié en pied. La taille, elle, reste la même partout : c'est elle
   * qui tient l'uniformité des entêtes.
   */
  forme?: 'ronde' | 'carree'
}): JSX.Element {
  const [ouverte, setOuverte] = useState(false)
  const cliquable = images.length > 0

  return (
    <>
      <Box
        onClick={() => cliquable && setOuverte(true)}
        onKeyDown={(e) => {
          if (cliquable && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault()
            setOuverte(true)
          }
        }}
        role={cliquable ? 'button' : undefined}
        tabIndex={cliquable ? 0 : -1}
        aria-label={cliquable ? 'Agrandir les visuels' : undefined}
        sx={{
          position: 'relative',
          flexShrink: 0,
          width: VIGNETTE_FICHE,
          height: VIGNETTE_FICHE,
          borderRadius: forme === 'ronde' ? '50%' : 2,
          overflow: 'hidden',
          display: 'grid',
          placeItems: 'center',
          bgcolor: 'action.hover',
          cursor: cliquable ? 'zoom-in' : 'default',
        }}
      >
        {images[0] ? (
          <Box
            component="img"
            src={images[0]}
            alt=""
            sx={{
              // ⚠️ Positionnement ABSOLU, et non `height: 100%` dans la
              // grille : la rangee de la grille se dimensionne sinon sur la
              // hauteur NATURELLE de l'image, si bien que `100%` se resout
              // contre elle — raisonnement circulaire, image qui deborde.
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        ) : (
          repli
        )}

        {images.length > 1 && (
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              textAlign: 'center',
              bgcolor: 'rgba(0,0,0,.6)',
              color: '#fff',
              fontSize: 9,
              lineHeight: 1.4,
            }}
          >
            1/{images.length}
          </Typography>
        )}
      </Box>

      <Dialog open={ouverte} onClose={() => setOuverte(false)} maxWidth="lg">
        <DialogContent sx={{ display: 'grid', placeItems: 'center', p: 4 }}>
          {/* Largeur bornée seulement : la hauteur est limitée dans le
              carrousel lui-même (78vh). L'ancienne formule multipliait la
              hauteur par 0,75, ce qui présupposait un format 3:4 — le visuel
              garde désormais SES proportions, quelles qu'elles soient. */}
          <PhotoSlider images={images} width="min(86vw, 720px)" />
        </DialogContent>
      </Dialog>
    </>
  )
}
